/**
 * Sticky Add to Cart Bar
 * Handles:
 * 1. Sticky button click → scroll to buy buttons
 * 2. Hide Back to Top button on product pages with sticky ATC
 * 3. Hide sticky bar when user scrolls past 70% of the footer
 * 4. Add to cart button states (loading, success)
 */

class StickyAddToCart {
  constructor() {
    this.stickyWrapper = document.querySelector('.sticky-addtocart-wrapper');
    this.stickyButton = document.querySelector('[data-sticky-atc-button]');
    this.backToTopButton = document.getElementById('back-to-top');

    if (!this.stickyWrapper || !this.stickyButton) return;

    this.init();
  }

  init() {
    this.setupStickyButtonClick();
    this.setupBackToTopVisibility();
    this.setupStickyBarVisibility();
  }

  /**
   * Sticky button click → scroll to buy buttons section
   */
  setupStickyButtonClick() {
    this.stickyButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const target = document.querySelector('.product-form__buttons') ||
                     document.querySelector('form[data-type="add-to-cart-form"]') ||
                     document.querySelector('.product-form__submit');

      if (!target) return;

      const header = document.querySelector('.section-header, .shopify-section-header, header');
      const stickyBarHeight = this.stickyWrapper.offsetHeight || 0;
      const headerHeight = header ? header.offsetHeight : 0;

      const stickyPosition = this.stickyWrapper.dataset.stickyPosition;
      let offset = 20;

      if (stickyPosition === 'top') {
        offset = headerHeight + stickyBarHeight + 20;
      } else {
        offset = headerHeight + 20;
      }

      const targetPosition = target.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });

      setTimeout(() => {
        const mainButton = document.querySelector('.product-form__submit:not([class*="sticky"])');
        if (mainButton) {
          mainButton.focus();
        }
      }, 500);
    });
  }

  /**
   * Hide Back to Top button on product pages where sticky ATC is enabled
   */
  setupBackToTopVisibility() {
    if (!this.backToTopButton) return;
    this.backToTopButton.classList.add('back-to-top--hidden-by-sticky');
    this.backToTopButton.style.display = 'none';
  }

  /**
   * Show/hide sticky bar based on scroll position
   * Hides when buy buttons are in viewport OR when scrolled past 70% of footer
   */
  setupStickyBarVisibility() {
    const buyButtons = document.querySelector('.product-form__buttons') ||
                       document.querySelector('form[data-type="add-to-cart-form"]');

    if (!buyButtons) return;

    const footer = document.querySelector('.shopify-section-group-footer-group') ||
                   document.querySelector('footer');

    let ticking = false;

    const updateVisibility = () => {
      const buyButtonsRect = buyButtons.getBoundingClientRect();

      let shouldShow = buyButtonsRect.bottom < 0;

      // Hide when user has scrolled past 70% of the footer
      if (shouldShow && footer) {
        const footerRect = footer.getBoundingClientRect();
        const footerVisibleHeight = window.innerHeight - footerRect.top;
        const seventyPercent = footer.offsetHeight * 0.7;

        if (footerVisibleHeight >= seventyPercent) {
          shouldShow = false;
        }
      }

      if (shouldShow) {
        this.stickyWrapper.classList.add('active');
      } else {
        this.stickyWrapper.classList.remove('active');
      }

      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(updateVisibility);
        ticking = true;
      }
    }, { passive: true });

    updateVisibility();
  }
}

/**
 * Add to Cart Button States
 * Handles loading and success states for the main add to cart button
 */
class AddToCartButtonStates {
  constructor() {
    this.successDuration = 2000;
    this.init();
  }

  init() {
    if (customElements.get('product-form')) {
      this.setupAllForms();
    } else {
      customElements.whenDefined('product-form').then(() => {
        this.setupAllForms();
      });
    }
  }

  setupAllForms() {
    document.querySelectorAll('product-form').forEach(form => {
      this.setupForm(form);
    });
  }

  setupForm(productForm) {
    const submitButton = productForm.querySelector('[name="add"]');
    if (!submitButton) return;

    const textSpan = this.getButtonTextElement(submitButton);
    if (textSpan && !submitButton.dataset.originalText) {
      submitButton.dataset.originalText = textSpan.textContent.trim();
    }

    this.interceptFormSubmit(productForm, submitButton);
  }

  getButtonTextElement(button) {
    const buttonText = button.querySelector('.button__text');
    if (buttonText) return buttonText;

    const directSpan = button.querySelector(':scope > span:not(.loading-overlay__spinner)');
    if (directSpan) {
      const nestedText = directSpan.querySelector('.button__text');
      if (nestedText) return nestedText;

      if (!directSpan.querySelector('.button__arrow')) {
        return directSpan;
      }

      const textContainer = directSpan.childNodes[0];
      if (textContainer && textContainer.nodeType === Node.TEXT_NODE) {
        const wrapper = document.createElement('span');
        wrapper.className = 'button__text';
        wrapper.textContent = textContainer.textContent.trim();
        directSpan.replaceChild(wrapper, textContainer);
        return wrapper;
      }
    }

    return null;
  }

  interceptFormSubmit(productForm, submitButton) {
    const originalOnSubmitHandler = productForm.onSubmitHandler.bind(productForm);

    productForm.onSubmitHandler = async (evt) => {
      evt.preventDefault();

      this.showLoadingState(submitButton);

      try {
        await originalOnSubmitHandler(evt);
        this.showSuccessState(submitButton);
      } catch (error) {
        this.resetButton(submitButton);
        console.error('Add to cart error:', error);
      }
    };
  }

  showLoadingState(button) {
    button.classList.add('loading');
    button.classList.remove('added');
    button.disabled = true;

    const spinner = button.querySelector('.loading-overlay__spinner');
    if (spinner) {
      spinner.classList.remove('hidden');
    }

    button.setAttribute('aria-label', 'Adding to cart');
  }

  showSuccessState(button) {
    button.classList.remove('loading');

    const spinner = button.querySelector('.loading-overlay__spinner');
    if (spinner) {
      spinner.classList.add('hidden');
    }

    setTimeout(() => {
      const textSpan = this.getButtonTextElement(button);
      if (textSpan) {
        textSpan.textContent = window.variantStrings?.added || 'Added';
      }

      button.classList.add('added');
      button.disabled = false;
      button.setAttribute('aria-label', 'Added to cart');

      setTimeout(() => {
        this.resetButton(button);
      }, this.successDuration);
    }, 300);
  }

  resetButton(button) {
    button.classList.remove('added', 'loading');
    button.disabled = false;

    const spinner = button.querySelector('.loading-overlay__spinner');
    if (spinner) {
      spinner.classList.add('hidden');
    }

    const textSpan = this.getButtonTextElement(button);
    if (textSpan && button.dataset.originalText) {
      textSpan.textContent = button.dataset.originalText;
    }

    button.removeAttribute('aria-label');
  }
}

function initStickyAddToCart() {
  new StickyAddToCart();
  new AddToCartButtonStates();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStickyAddToCart);
} else {
  initStickyAddToCart();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('product-form') || event.target.querySelector('.sticky-addtocart-wrapper')) {
    initStickyAddToCart();
  }
});
